import CloseIcon from '@mui/icons-material/Close';
import { Alert, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import type { AtoReport } from '../../data/types';
import { buildAiirRecords } from '../../domain/taxOutputs';
import { money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import DataTable from '../../ui/DataTable';
import { StatusChip } from '../../ui/primitives';

function AiirTable({ report }: { report: AtoReport }) {
  const { state } = useRegistry();
  const records = useMemo(() => buildAiirRecords(state, report.period), [state, report.period]);
  const showWarnings = report.issues > 0;
  const code = (fundId: string) => state.funds.find((fund) => fund.id === fundId)?.code ?? fundId;
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {records.length} payee records · gross{' '}
        {money(records.reduce((sum, record) => sum + record.gross, 0))} · TFN withheld{' '}
        {money(
          records.reduce((sum, record) => sum + record.tfnWithheld, 0),
          true,
        )}
      </Typography>
      <DataTable
        rows={
          showWarnings
            ? [...records].sort((a, b) => Number(Boolean(b.warning)) - Number(Boolean(a.warning)))
            : records
        }
        rowKey={(row) => `${row.holderId}-${row.fundId}`}
        dense
        pageSize={12}
        columns={[
          {
            key: 'payee',
            label: 'Payee',
            render: (row) => (
              <>
                <Typography variant="body2">{row.payeeName}</Typography>
                {showWarnings && row.warning && <StatusChip status="PENDING" label={row.warning} />}
              </>
            ),
          },
          { key: 'trust', label: 'Trust', render: (row) => code(row.fundId) },
          { key: 'id', label: 'TFN / ABN', render: (row) => row.identifier },
          { key: 'res', label: 'Residency', render: (row) => row.residency },
          { key: 'gross', label: 'Gross', align: 'right', render: (row) => money(row.gross, true) },
          {
            key: 'tfn',
            label: 'TFN withheld',
            align: 'right',
            render: (row) => money(row.tfnWithheld, true),
          },
          {
            key: 'nr',
            label: 'NR withheld',
            align: 'right',
            render: (row) => money(row.nrWithheld, true),
          },
        ]}
      />
    </>
  );
}

function HolderList({ report }: { report: AtoReport }) {
  const { state } = useRegistry();
  const holders = state.holders.filter((holder) => holder.tenantId === 't-fides');
  const rows =
    report.kind === 'TFN_REPORT'
      ? holders
          .filter((holder) => holder.tfnStatus === 'QUOTED')
          .slice(0, report.records || undefined)
      : holders.filter((holder) =>
          report.kind === 'FATCA' ? holder.fatca === 'REPORTABLE' : holder.crs === 'REPORTABLE',
        );
  return (
    <DataTable
      rows={rows}
      rowKey={(row) => row.id}
      dense
      pageSize={12}
      emptyMessage="No reportable records"
      columns={[
        {
          key: 'name',
          label: report.kind === 'TFN_REPORT' ? 'Investor' : 'Account holder',
          render: (row) => row.name,
        },
        report.kind === 'TFN_REPORT'
          ? { key: 'tfn', label: 'TFN', render: (row) => `••• ••• ${row.tfnLast3}` }
          : { key: 'jur', label: 'Jurisdiction', render: (row) => row.residency },
        { key: 'acct', label: 'Account', render: (row) => row.accountNumber },
        { key: 'type', label: 'Holder type', render: (row) => row.type.toLowerCase() },
      ]}
    />
  );
}

const TITLES: Record<AtoReport['kind'], string> = {
  AIIR: 'Annual investment income report records',
  TFN_REPORT: 'TFN report records',
  FATCA: 'FATCA reportable accounts',
  CRS: 'CRS reportable accounts',
};

export default function AtoRecordsDialog({
  report,
  onClose,
}: {
  report: AtoReport | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(report)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 7 }}>
        {report ? `${TITLES[report.kind]} · ${report.period}` : ''}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Illustrative fields drawn from the register. The lodged file follows the ATO specification
          and is validated before lodgement.
        </Alert>
        {report &&
          (report.kind === 'AIIR' ? <AiirTable report={report} /> : <HolderList report={report} />)}
      </DialogContent>
    </Dialog>
  );
}
